/** Shared constants carried over from the prototype. */

import type { PaymentType } from "./types.js";

/**
 * The company, as of 2026-08-09 (ADR 0033). Superseded "Supreme Roofing &
 * Renovations", which itself superseded "The Roofing Dudes".
 *
 * This is the fallback. Anything a customer reads takes the name from
 * `company_settings`, so a fourth rebrand is a row edit rather than a deploy.
 */
export const COMPANY_NAME = "CopperRidge Roofing & Exteriors LLC";

/** Brand colours — ADR 0022. */
export const BRAND_RED = "#ef2b4b";
export const BRAND_GREY = "#4a4a4a";

export const NAV_ITEMS = [
  "Contacts",
  "Prospects",
  "Jobs",
  "Ongoing",
  "Payouts",
  "Cal",
  // The phone is not a tab any more. It floats in the corner of every screen
  // as a bubble that opens into Messages, Recents and Voicemail (ADR 0321,
  // which took it out of this list where ADR 0310 had put it).
  // People who send us work, as distinct from people we roof (ADR 0067). Its
  // own tab because a partner has no job, no ladder and no place in a
  // pipeline — putting a realtor in Contacts would put them in Prospects and
  // in somebody's follow-up clock.
  //
  // Always drawn since 2026-10-04: with the network switched off it is the
  // business card file and nothing else (ADR 0262, amending ADR 0144).
  "Partners",
  "Reports",
  // Everything the AI found on its own, as three named AI employees, for
  // everyone (ADR 0259). Iris's inbox of customers waiting on a reply used
  // to be its own Messages tab.
  "AI Team",
  // What the advertising earned and what is owed for it (ADR 0074). Beside
  // Reports rather than inside it: Reports answers "which of our numbers is
  // working", and this one is a bill somebody is waiting to be paid.
  "Marketing",
  "Tools",
] as const;
export type NavItem = (typeof NAV_ITEMS)[number];

/**
 * The tabs actually drawn across the top, in the order above.
 *
 * None is absent any more. Messages went with ADR 0259 (its list is the AI
 * Team page's inbox), and Partners, once hidden while the partner network
 * was off (ADR 0144), is always drawn since ADR 0262: the switch decides how
 * much of the section it opens onto, not whether the tab is there.
 *
 * A field login gets fewer (ADR 0322): pass the viewer's profile.
 */
export function navItems(
  viewer?: { field_view?: boolean | null } | null,
): readonly NavItem[] {
  return viewer?.field_view === true ? FIELD_NAV_ITEMS : NAV_ITEMS;
}

/**
 * The tabs a field login does not get (ADR 0322).
 *
 * Jonathon, 2026-10-30, on Carlos: "he doesn't need to see partners,
 * reports, ai team, or marketing." Everything else stays, in the same order,
 * so that a screen he is shown over somebody's shoulder is the screen he
 * has. Drawn less, not locked: the marker hides a tab and grants nothing.
 */
export const FIELD_HIDDEN_NAV: readonly NavItem[] = [
  "Partners",
  "Reports",
  "AI Team",
  "Marketing",
];

/**
 * The cards on a job a field login does not get (ADR 0322).
 *
 * Jonathon, 2026-10-30, on Carlos and the job page: "he doesn't need to see
 * activity or partners". Named by the card's title on the job page.
 */
export const FIELD_HIDDEN_JOB_CARDS: readonly string[] = [
  "Activity",
  "Partners",
];

/** The field menu: `NAV_ITEMS` without the four above, in the same order. */
export const FIELD_NAV_ITEMS: readonly NavItem[] = NAV_ITEMS.filter(
  (item) => !FIELD_HIDDEN_NAV.includes(item),
);

/**
 * The units a line can carry. SF is a deck's square feet, and a siding or
 * flat-roof line priced by the foot; PK is a pack, as the deck's bolts,
 * washers and nuts are sold. A stored unit missing here has no option in the
 * unit pickers, so they show it as blank or EA (PR #873's review).
 */
export const UNITS = [
  "EA",
  "SQ",
  "SF",
  "BD",
  "RL",
  "BX",
  "PK",
  "LF",
  "GAL",
  "HR",
] as const;

/**
 * How an expense was paid.
 *
 * The stored value is a code, not a label, so the wording on screen can change
 * without orphaning rows that already carry the old one. "Check" is no longer
 * offered but still renders, because rows written before it was dropped are
 * not wrong — they are history.
 */
export const PAYMENT_TYPES: readonly PaymentType[] = [
  "Cash",
  "CreditCard",
  "ACH",
];

export const PAYMENT_TYPE_LABEL: Record<string, string> = {
  Cash: "Cash",
  CreditCard: "Credit/Debit Card",
  ACH: "ACH",
  Check: "Check (retired)",
};

/**
 * Four fixed payment slots per estimate, created by trigger.
 *
 * First and Final carry their trigger text by default — the two that are always
 * the same — and the middle two are typed if the job needs them.
 */
export const PAYMENT_SLOT_LABEL: Record<number, string> = {
  1: "First Payment",
  2: "Second Payment",
  3: "Third Payment",
  4: "Final Payment",
};

export const MATERIAL_ORDER_STATUSES = [
  "not_placed",
  "ordered",
  "picked_up",
  "paid",
] as const;

/**
 * Nothing submits a work order to anyone, and nobody approves one. It is
 * either on the calendar or it is not.
 */
export const WORK_ORDER_STATUSES = [
  "not_scheduled",
  "scheduled",
  "completed",
  "paid",
] as const;

/**
 * What the company sells at.
 *
 * A starting point, not a rule: the gross profit percentage on the estimate is
 * clickable and moves the whole job. It lives here because two screens price
 * off it — the generated estimate and a deck over — and two copies of a number
 * like this drift.
 */
export const TARGET_MARGIN = 0.4;

export const COMPANYCAM_URL = "https://app.companycam.com";

/** Address autocomplete is biased to Alabama. */
export const PHOTON_URL = "https://photon.komoot.io/api/";
export const ALABAMA_BIAS = {
  lat: 33.2857,
  lon: -86.8097,
  zoom: 12,
  location_bias_scale: 0.7,
};

export const US_STATES: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

/**
 * The states, for a dropdown — code and name, in alphabetical order by name.
 *
 * The state used to be a free-text box, which meant "AL", "Al", "Ala" and
 * "Alabama" were four different states as far as any list or report was
 * concerned. Derived from US_STATES so there is one list of states in this
 * codebase and not two.
 */
export const US_STATE_OPTIONS: readonly { code: string; name: string }[] =
  Object.entries(US_STATES)
    .map(([name, code]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Appointment times, every 15 minutes from 6am to 8pm.
 *
 * Inspections are booked in quarter-hour slots rather than by typing a time,
 * which is what a free-text time field invited.
 */
export const TIME_SLOTS: readonly { value: string; label: string }[] = (() => {
  const slots: { value: string; label: string }[] = [];
  for (let minutes = 6 * 60; minutes <= 20 * 60; minutes += 15) {
    const h24 = Math.floor(minutes / 60);
    const m = minutes % 60;
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    slots.push({
      value: `${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      label: `${h12}:${String(m).padStart(2, "0")} ${h24 < 12 ? "AM" : "PM"}`,
    });
  }
  return slots;
})();

/** Roof pitches, low to steep. "Flat" covers anything under 1/12. */
export const PITCH_OPTIONS: readonly string[] = [
  "Flat",
  ...Array.from({ length: 16 }, (_, i) => `${i + 1}/12`),
];

/**
 * Waste allowances offered on a roof layout.
 *
 * These match the waste values already carried on the product catalogue, which
 * uses 0, 10, 12.5 and 15. The rest are the usual steps either side.
 */
export const WASTE_PCT_OPTIONS: readonly number[] = [
  0, 5, 10, 12.5, 15, 18, 20, 25,
];

/**
 * The measurement form, in order.
 *
 * `key` is the column on `job_measurements`. Where a field feeds auto-quantity,
 * `productKey` is the matching `products.measurement` value — see the migration
 * that added these columns.
 */
export interface RoofLayoutField {
  key: string;
  label: string;
  productKey?: string;
}

export const ROOF_LAYOUT_FIELDS: readonly RoofLayoutField[] = [
  { key: "squares", label: "Squares", productKey: "TOTAL_PITCH_AREA_SQUARES" },
  { key: "eave_lf", label: "Eave lf", productKey: "EAVES_LENGTH" },
  { key: "rake_lf", label: "Rake lf", productKey: "RAKES_LENGTH" },
  { key: "hip_lf", label: "Hip lf", productKey: "HIPS_LENGTH" },
  { key: "ridge_lf", label: "Ridge lf", productKey: "RIDGES_LENGTH" },
  { key: "ridge_vent_lf", label: "Ridge vent lf" },
  { key: "valley_lf", label: "Valley lf", productKey: "VALLEYS_LENGTH" },
  { key: "gutter_lf", label: "Gutter lf" },
  { key: "downspout_ea", label: "Downspouts" },
  { key: "downspout_lf", label: "Downspout lf" },
  {
    key: "step_flashing_lf",
    label: "Step Flashing LF",
    productKey: "STEP_FLASHING_LENGTH",
  },
  {
    key: "flashing_lf",
    label: "Flashing LF",
    productKey: "WALL_FLASHING_LENGTH",
  },
  {
    key: "transition_lf",
    label: "Transition LF",
    productKey: "TRANSITIONS_LENGTH",
  },
  { key: "two_story_squares", label: "Two story squares" },
  {
    key: "flat_squares",
    label: "Flat squares",
    productKey: "TOTAL_FLAT_AREA_SQUARES",
  },
  { key: "flat_perimeter_lf", label: "Flat perimeter lf" },
  /*
   * The parapet walls, as two numbers.
   *
   * A flat roof's perimeter is two different things and they must not overlap.
   * Jonathon, 2026-08-31: "coping goes on walls. drip edge and termination bar
   * goes on eaves and rakes. you don't use drip edge or termination bar where
   * coping would go since the coping serves that purpose." So eave and rake are
   * the *open* edges only, and a run of parapet is entered here instead —
   * asked which way round it should work, he chose exactly that.
   *
   * The height is here because the membrane always runs up the wall, so the
   * wall is roof as far as the TPO order is concerned and there is no other
   * place to learn how much of it there is. See `flatRoofAreas`.
   */
  { key: "wall_length_lf", label: "Parapet wall lf" },
  { key: "wall_height_ft", label: "Parapet wall height ft" },
  /*
   * Two steep boxes, not ten.
   *
   * This used to be a box per pitch from 7/12 to 16/12, which asked the rep for
   * ten numbers to answer a question with two answers. The steep charge has two
   * bands and always has (`ROOF_SLOTS`, and the rates behind them): 8/12 to
   * 12/12 costs one thing, 13/12 and up costs another, and 7/12 costs nothing
   * extra at all — that box fed no price anywhere and was pure typing.
   *
   * Jonathon, 2026-08-13: "there really just needs to be a steep charge pitch
   * of 8/12-12/12 (one cell) and 13/12+ (one cell)."
   *
   * A measurement report is still read per pitch; `parseRoofReport` adds the
   * columns up into these two before the form ever sees them.
   */
  { key: "steep_mid_squares", label: "Steep 8/12 – 12/12 SQ" },
  { key: "steep_high_squares", label: "Steep 13/12 and up SQ" },
];
